/** Quote discovery does not grant permission or capability to execute a swap. */
import type { WalletSummary } from './types';
import { chainFamily } from './network';
import { isValidSolanaAddress } from '@/chains/solana';
import { isValidSuiAddress } from '@/chains/sui';

export const EVM_SWAP_CHAINS: Record<number, string> = {
  11155111: 'sepolia', 84532: 'base-sepolia',
  1: 'ethereum', 56: 'bsc', 8453: 'base', 999: 'hyperevm',
};

export const QUOTABLE_SWAP_CHAINS: Record<number, string> = {
  ...EVM_SWAP_CHAINS, 792703809: 'solana', 1999: 'sui',
};

/** Solana mints and Move type names are case-sensitive; EVM addresses are not. */
export function swapCurrencyKey(chainId: number | null, address: string): string {
  if (chainId === 792703809) return address;
  if (chainId === 1999) {
    const parts = /^(0x[0-9a-fA-F]{1,64})::([A-Za-z_][A-Za-z0-9_]*)::([A-Za-z_][A-Za-z0-9_]*)$/.exec(address);
    return parts ? `0x${parts[1].slice(2).toLowerCase().padStart(64, '0')}::${parts[2]}::${parts[3]}` : address;
  }
  return address.toLowerCase();
}

export function isEvmAddress(address?: string | null): boolean {
  return !!address && /^0x[0-9a-fA-F]{40}$/.test(address);
}

/** Concrete mainnet keys are required for the two non-EVM provider origins. */
export function isSwapQuoteWallet(wallet: WalletSummary): boolean {
  if (chainFamily(wallet.chain) === 'EVM') return isEvmAddress(wallet.address);
  if (wallet.chain === 'solana') return isValidSolanaAddress(wallet.address ?? '');
  if (wallet.chain === 'sui') return isValidSuiAddress(wallet.address ?? '');
  return false;
}

/** Callers must additionally filter wallet lifecycle and active deployment tier. */
export function walletSupportsSwapOrigin(wallet: WalletSummary, chainId: number): boolean {
  if (!isSwapQuoteWallet(wallet)) return false;
  if (chainId in EVM_SWAP_CHAINS) return chainFamily(wallet.chain) === 'EVM';
  return QUOTABLE_SWAP_CHAINS[chainId] === wallet.chain;
}
